import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Package, Clock, CheckCircle, XCircle, MapPin, Phone, 
  Calendar, Store, ChevronLeft, CreditCard, ShoppingBag, Truck
} from "lucide-react";
import api from "./axiosConfig";
import usePlatformFees from "./hooks/usePlatformFees";
import { initClientSocket } from "./socketClient";

export default function DetailsCommande() {
  const { id: orderNumber } = useParams();
  const navigate = useNavigate();
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(true);

  const { serviceFee, deliveryFee, loading: loadingFees } = usePlatformFees();

  const getStatusConfig = (status) => {
    switch (status) {
      case "en_attente":
        return { label: "En attente", color: "bg-slate-100 text-slate-600", accent: "bg-slate-400", icon: Clock };
      case "en_preparation":
        return { label: "En préparation", color: "bg-orange-100 text-orange-600", accent: "bg-orange-500", icon: Package };
      case "prete":
        return { label: "Prête", color: "bg-green-100 text-green-600", accent: "bg-green-500", icon: CheckCircle };
      case "livree":
        return { label: "Livrée", color: "bg-blue-100 text-blue-600", accent: "bg-blue-500", icon: CheckCircle };
      case "annulee":
        return { label: "Annulée", color: "bg-red-50 text-red-500", accent: "bg-red-400", icon: XCircle };
      default:
        return { label: status, color: "bg-gray-100 text-gray-700", accent: "bg-gray-400", icon: Package };
    }
  };

  useEffect(() => {
    fetchCommande();
    const phone = localStorage.getItem("customerPhone") || "";
    const socket = initClientSocket(phone);
    const onUpdate = (payload) => {
      if (String(payload?.orderNumber) === String(orderNumber)) fetchCommande();
    };
    socket.on("client_order_updated", onUpdate);
    return () => socket.off("client_order_updated", onUpdate);
  }, [orderNumber]);

  const fetchCommande = async () => {
    try {
      const res = await api.get(`/orders/by-number/${orderNumber}`);
      setCommande(res.data.order);
    } catch (err) {
      setCommande(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#FAFAFA]">
      <div className="w-10 h-10 border-4 border-[#f34141] border-t-transparent rounded-full animate-spin mb-4" />
      <p className="font-bold text-slate-400">Récupération de votre ticket...</p>
    </div>
  );

  if (!commande) return (
    <div className="max-w-3xl px-6 pt-40 mx-auto text-center">
      <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 text-red-500 rounded-full bg-red-50">
        <XCircle size={40} />
      </div>
      <h2 className="mb-2 text-2xl font-black text-slate-900">Oups !</h2>
      <p className="mb-8 font-medium text-slate-500">Impossible de trouver cette commande.</p>
      <button onClick={() => navigate("/commandes")} className="px-8 py-4 font-black text-white bg-slate-900 rounded-2xl">Retourner aux commandes</button>
    </div>
  );

  const subtotal = commande.subtotal || 0;
  const finalTotal = subtotal + serviceFee + deliveryFee;
  const statusConfig = getStatusConfig(commande.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen px-4 pb-40 font-sans pt-28 bg-[#FAFAFA]">
      <div className="max-w-2xl mx-auto">
        
        {/* HEADER & RETOUR */}
        <div className="flex items-center gap-4 mb-8">
            <button onClick={() => navigate("/commandes")} className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-50 text-slate-400 hover:text-[#f34141] transition-colors">
                <ChevronLeft size={24} />
            </button>
            <div>
                <h1 className="text-2xl font-black leading-none text-slate-900">Détails Commande</h1>
                <p className="mt-1 text-xs font-bold tracking-widest uppercase text-slate-400">Récapitulatif Officiel</p>
            </div>
        </div>

        {/* CARTE STATUT & MARCHAND */}
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-50 mb-6 relative overflow-hidden">
            <div className={`absolute top-0 right-0 px-6 py-2 rounded-bl-3xl font-black text-[10px] uppercase tracking-widest ${statusConfig.color}`}>
                {statusConfig.label}
            </div>

            <div className="flex items-center gap-4 mb-8">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${statusConfig.color}`}>
                    <StatusIcon size={28} />
                </div>
                <div>
                    <h2 className="text-3xl font-black text-slate-900">#{commande.orderNumber}</h2>
                    <p className="text-sm font-bold text-slate-400">
                        {new Date(commande.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 pt-6 border-t md:grid-cols-2 border-gray-50">
                <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-slate-50 rounded-xl text-slate-400">
                        <Store size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Magasin</p>
                        <p className="font-black text-slate-700">{commande.merchantId?.name || commande.merchantName || "Supermarché"}</p>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-slate-50 rounded-xl text-slate-400">
                        <Truck size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Réception</p>
                        <p className="font-black text-slate-700">{commande.fulfillmentType === "delivery" ? "Livraison" : "Click & Collect"}</p>
                    </div>
                </div>
            </div>
        </div>

        {/* ADRESSE & PAIEMENT */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
            <div className="bg-white p-6 rounded-[2rem] border border-gray-50 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <MapPin size={18} className="text-[#f34141]" />
                    <h3 className="text-sm font-black tracking-tight uppercase text-slate-800">Adresse</h3>
                </div>
                <p className="text-sm font-bold leading-relaxed text-slate-500">{commande.address}</p>
                <p className="mt-2 text-sm font-black text-slate-700">{commande.customerPhone}</p>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-gray-50 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <CreditCard size={18} className="text-blue-500" />
                    <h3 className="text-sm font-black tracking-tight uppercase text-slate-800">Paiement</h3>
                </div>
                <p className="text-sm font-black text-slate-700">
                    {commande.paymentMethod === "cash" ? "💰 Espèces" : "📱 Paiement Mobile"}
                </p>
                <div className="mt-3">
                    <span className="px-3 py-1 bg-green-50 text-green-600 text-[10px] font-black rounded-lg uppercase border border-green-100">
                        Confirmé
                    </span>
                </div>
            </div>
        </div>

        {/* ARTICLES */}
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-50 mb-6">
            <div className="flex items-center gap-3 mb-6">
                <ShoppingBag size={20} className="text-[#f34141]" />
                <h3 className="text-lg font-black text-slate-900">Articles commandés</h3>
            </div>
            <div className="space-y-4">
                {commande.items?.map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-4 border-b border-gray-50 last:border-none">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center font-black text-[#f34141] text-sm">
                                {item.quantity}x
                            </div>
                            <p className="font-bold text-slate-700">{item.name}</p>
                        </div>
                        <p className="font-black text-slate-900">{(item.price * item.quantity)} <span className="text-[10px] text-slate-400">MRU</span></p>
                    </div>
                ))}
            </div>
        </div>

        {/* RÉCAPITULATIF FINANCIER (MODIFIÉ : FOND BLANC PREMIUM) */}
        <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-gray-100 border-2 border-[#f34141]/5 relative overflow-hidden">
            <div className="relative z-10 space-y-4">
                <div className="flex justify-between text-sm font-bold text-slate-400">
                    <span>Sous-total</span>
                    <span className="text-slate-700">{subtotal} MRU</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-slate-400">
                    <span>Frais de service Souqi</span>
                    <span className="text-slate-700">{serviceFee} MRU</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-slate-400">
                    <span>Livraison</span>
                    <span className="text-slate-700">{deliveryFee} MRU</span>
                </div>
                
                <div className="flex items-end justify-between pt-6 mt-6 border-t border-gray-100 border-dashed">
                    <div>
                        <p className="text-[10px] font-black text-[#f34141] uppercase tracking-[0.2em] mb-1">Total à régler</p>
                        <p className="text-4xl font-black tracking-tighter text-slate-900">{finalTotal} <span className="text-lg text-[#f34141] ml-1">MRU</span></p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-2xl">
                        <CheckCircle size={28} className="text-green-500" />
                    </div>
                </div>
            </div>
        </div>

        {/* PREUVE DE PAIEMENT CORRIGÉE */}
        {commande.paymentProof && (
            <div className="mt-8 bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm">
                <h3 className="mb-4 text-sm font-black tracking-widest uppercase text-slate-800">Preuve de paiement</h3>
                <img
                    src={commande.paymentProof.startsWith('http') 
                        ? commande.paymentProof 
                        : `${process.env.REACT_APP_API_URL}${commande.paymentProof}`}
                    alt="Preuve"
                    className="object-contain w-full h-auto border rounded-2xl border-gray-50"
                />
            </div>
        )}

      </div>

      {/* BOUTON RETOUR FIXE */}
      <div className="fixed z-50 w-full max-w-md px-6 -translate-x-1/2 bottom-8 left-1/2">
        <button 
            onClick={() => navigate("/commandes")}
            className="flex items-center justify-center w-full gap-2 py-5 font-black transition-all bg-white border border-gray-100 shadow-2xl rounded-2xl text-slate-900 hover:bg-slate-50"
        >
            <ChevronLeft size={20} />
            Retour à l'historique
        </button>
      </div>
    </div>
  );
}

